<script lang="ts" setup>
import { email, helpers, minLength, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

const { t } = useI18n();
const localePath = useLocalePath();
const toast = useToast();
const { fetch: sendContactRequest } =
  api.contactRequest.useSendContactRequest();

const defaultFormData = {
  fullName: "",
  email: "",
  phoneNumber: "",
  requestText: "",
  promocode: "",
};

const emits = defineEmits(["closeDialog"]);

const formData = reactive({ ...defaultFormData });

const rules = {
  fullName: {
    required,
    minLength: minLength(3),
  },
  email: {
    required,
    email,
  },
  phoneNumber: {
    required,
    regex: helpers.withMessage(
      "Invalid phone number format",
      helpers.regex(/^[0-9-+()]+$/),
    ),
    minLength: minLength(4),
  },
};

const vuelidate = useVuelidate(rules, formData);

const onSubmitButtonClick = async () => {
  vuelidate.value.$touch();
  if (vuelidate.value.$error) return;
  try {
    await sendContactRequest({
      email: formData.email,
      fullName: formData.fullName,
      phoneNumber: formData.phoneNumber,
      requestText: formData.requestText,
    });
    toast.success("Your contact request has been successfully submitted");
  } catch (err) {
    toast.error(
      "An error occurred while trying to submit your contact request. Please, try again later or contact us via email",
    );
  }
  Object.assign(formData, defaultFormData);
  emits("closeDialog");
};
</script>

<template>
  <Suspense>
    <form>
      <div>
        <h1 class="text-center">
          {{ t("contactForm.title") }}
        </h1>
        <div
          class="grid grid-cols-2 grid-rows-4 gap-x-10 gap-y-3 children:col-start-1 children:col-end-1 lg:w-[100%] <lg:flex <lg:flex-col <lg:items-stretch"
        >
          <PageTextInput
            v-model="formData.fullName"
            :placeholder="t('contactForm.fullName')"
            :title="t('contactForm.fullName')"
            :error="
              vuelidate.fullName.$error
                ? vuelidate.fullName.$errors[0].$message
                : ''
            "
          />
          <PageTextInput
            v-model="formData.phoneNumber"
            :placeholder="t('contactForm.phoneNumber')"
            :title="t('contactForm.phoneNumber')"
            :error="
              vuelidate.phoneNumber.$error
                ? vuelidate.phoneNumber.$errors[0].$message
                : ''
            "
          />
          <PageTextInput
            v-model="formData.email"
            :placeholder="t('contactForm.email')"
            :title="t('contactForm.email')"
            :error="
              vuelidate.email.$error ? vuelidate.email.$errors[0].$message : ''
            "
          />
          <PageTextInput
            v-model="formData.promocode"
            :placeholder="t('contactForm.promocode')"
            :title="t('contactForm.promocode')"
          />
          <PageTextInput
            v-model="formData.requestText"
            class="!col-start-2 !col-end-3 row-start-1 row-end-5 <lg:h-[150px]"
            :placeholder="t('contactForm.requestText')"
            type="textarea"
            :resize="false"
            :title="t('contactForm.requestText')"
          />
        </div>
        <div class="text-black/50 mt-4">
          * Wyrażam zdogę na przetwarzanie moich danych osobowych zgodnie z
          <NuxtLink
            :to="localePath('/documents/discount-codes')"
            target="_blank"
          >
            Regulaminem
          </NuxtLink>
          i
          <NuxtLink :to="localePath('/documents/rodo')" target="_blank">
            RODO
          </NuxtLink>
          firmy Good Balance.
        </div>
        <div class="flex justify-center !w-full mt-4">
          <PageButton @click.prevent="onSubmitButtonClick">
            {{ t("contactForm.sendRequest") }}
          </PageButton>
        </div>
      </div>
    </form>
  </Suspense>
</template>

<style lang="scss" scoped>
@import "~/assets/scss/_media";

a {
  color: rgba(0, 0, 0, 0.5);
  text-decoration: underline;
  cursor: pointer;
}

#contactDialog {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  opacity: 0;
  width: 100vw;
  height: 100dvh;
  transition: opacity 0.25s;
  z-index: -99;

  overflow-x: hidden;
  overflow-y: scroll;

  .form-wrapper > * {
    margin-top: 20px !important;
  }

  .background {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: -100;
    width: 100vw;
    height: var(--window-inner-height);
    background-color: rgba($color: #000000, $alpha: 0.5);
    transition: opacity 0.25s;

    @include media("max", "md") {
      display: none;
    }
  }

  &.active {
    z-index: 100;
    opacity: 1;

    .background {
      z-index: 99;
      opacity: 1;

      @include media("max", "md") {
        right: 20px;
        top: 63px;
      }
    }

    .dialog-form-container {
      -webkit-overflow-scrolling: touch;
      overflow-scrolling: touch;
      z-index: 101;
      opacity: 1;
    }
  }

  .dialog-form-container {
    opacity: 0;
    transition: opacity 0.25s;
    height: fit-content;
    border-radius: 15px;
    background-color: var(--color-light);
    box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.51);
    padding: 40px 0;
    position: relative;

    @include media("max", "md") {
      height: var(--window-inner-height);
    }

    .close-button {
      position: absolute;
      right: 20px;
      top: 20px;

      @include media("max", "md") {
        right: 20px;
        top: 63px;
      }

      &:hover {
        cursor: pointer;
      }
    }

    form {
      display: grid;
      grid-auto-rows: 1fr;
      grid-template-columns: 1fr;
      padding-inline: 50px;

      h1 {
        color: var(--color-darker);
        font-size: 27px;
        margin-bottom: 20px;
      }

      .input-block {
        display: grid;
        width: 300px;
        margin-top: 15px;

        label {
          color: var(--color-darker);
          font-size: 16px;
          margin-bottom: 5px;
        }

        input {
          height: 35px;
          outline: none;
          border: 1px solid #cecece;
          border-radius: 6px;
          background-color: #f0f0f0;
          padding: 0 10px;

          &::placeholder {
            color: #7c7c7c;
          }

          &:focus {
            border: 1px solid #b1b1b1;
          }
        }
      }

      button {
        margin-top: 30px;
      }
    }
  }
}
</style>
